// Huntrack workflow engine — outcome-driven call logging rules.
// Pure functions (no I/O) so they can be unit-tested. See docs/huntrack-target-workflow.md §4.

// Configurable defaults (decision: WIB, Not Interested -> Nurture, retry +1 business day, max 5).
export const WORKFLOW_CONFIG = {
  timezone: 'Asia/Jakarta',
  retryInBusinessDays: 1,
  maxCallAttempts: 5,
  notInterestedPolicy: 'nurture', // 'nurture' | 'closed_lost'
  findPicInDays: 2,
  dataFixInDays: 3,
}

// Canonical call outcomes + their conditional fields + automation.
// task.when: 'field' = due comes from a form datetime field; 'offset' = now + N business/calendar days
export const CALL_OUTCOMES = [
  {
    id: 'no_answer', label: 'No Answer', icon: '📵',
    fields: [],
    callLog: { outcome: 'No Answer' },
    stageTo: 'Attempting Contact',
    task: { type: 'retry_call', when: 'offset', businessDays: true, days: WORKFLOW_CONFIG.retryInBusinessDays, title: 'Retry call', priority: 'medium' },
  },
  {
    id: 'call_later', label: 'Busy / Call Later', icon: '⏰',
    fields: ['callback_at'],
    callLog: { outcome: 'No Answer' }, // existing enum has no "busy"; store note separately
    stageTo: 'Attempting Contact',
    task: { type: 'callback', when: 'field', field: 'callback_at', title: 'Callback (diminta prospect)', priority: 'high' },
  },
  {
    id: 'wrong_pic', label: 'Connected — Wrong PIC', icon: '🔀',
    fields: ['correct_contact'],
    callLog: { outcome: 'Connected — Needs More Info' },
    stageTo: 'Connected',
    task: { type: 'find_pic', when: 'offset', days: WORKFLOW_CONFIG.findPicInDays, title: 'Cari decision-maker yang tepat', priority: 'medium' },
  },
  {
    id: 'interested', label: 'Connected — Interested', icon: '🔥',
    fields: ['need', 'service_interest', 'bant_score', 'next_action', 'next_action_at'],
    callLog: { outcome: 'Connected — Interested', lead_status: 'Qualified' },
    stageTo: 'Connected', // -> Qualified if bant>=3 (handled in planner)
    task: { type: 'next_action', when: 'field', field: 'next_action_at', title: 'Next action', priority: 'high' },
  },
  {
    id: 'not_interested', label: 'Connected — Not Interested', icon: '🙅',
    fields: ['reason'],
    callLog: { outcome: 'Connected — Not Interested Now', lead_status: 'Not Interested' },
    stageTo: 'Nurture', // per config.notInterestedPolicy
    task: null,
  },
  {
    id: 'meeting', label: 'Meeting Scheduled', icon: '📅',
    fields: ['meeting_at', 'meeting_purpose'],
    callLog: { outcome: 'Connected — Very Interested', meeting_set: 'Yes', lead_status: 'Qualified' },
    stageTo: 'Meeting Scheduled',
    task: { type: 'meeting', when: 'field', field: 'meeting_at', title: 'Meeting discovery/demo', priority: 'high' },
  },
  {
    id: 'invalid', label: 'Invalid Contact', icon: '⚠️',
    fields: ['invalid_field'],
    callLog: { outcome: 'Wrong Number' },
    stageTo: null,
    task: { type: 'data_fix', when: 'offset', days: WORKFLOW_CONFIG.dataFixInDays, title: 'Perbaiki/verifikasi kontak', priority: 'low' },
    cancelOutreach: true,
  },
  {
    id: 'dnc', label: 'Do Not Contact', icon: '🚫',
    fields: ['reason', 'dnc_source'],
    callLog: { outcome: 'Connected — Not Interested Now', lead_status: 'Not Interested' },
    stageTo: 'Disqualified',
    task: null,
    suppress: true,
    cancelOutreach: true,
  },
]

export const OUTCOME_FIELD_LABELS = {
  callback_at: 'Waktu callback diminta',
  correct_contact: 'Kontak/PIC yang benar (jika tahu)',
  need: 'Kebutuhan bisnis prospek',
  service_interest: 'Layanan yang diminati',
  bant_score: 'BANT score (0–4)',
  next_action: 'Next action',
  next_action_at: 'Tanggal next action',
  reason: 'Alasan',
  meeting_at: 'Tanggal & waktu meeting',
  meeting_purpose: 'Tujuan meeting',
  invalid_field: 'Data yang salah (nomor/email)',
  dnc_source: 'Sumber permintaan (siapa yang minta)',
}

export function getOutcome(id) {
  return CALL_OUTCOMES.find(o => o.id === id)
}

// Task types that are "outreach" and should be cancelled on Invalid/DNC.
const OUTREACH_TASK_TYPES = ['retry_call', 'callback', 'next_action', 'find_pic']

// Add N calendar days to a YYYY-MM-DD; if businessDays, skip Sat/Sun.
export function addDaysISO(baseDateStr, n, businessDays = false) {
  const d = new Date(baseDateStr + 'T09:00:00')
  let added = 0
  while (added < n) {
    d.setDate(d.getDate() + 1)
    const day = d.getDay()
    if (businessDays && (day === 0 || day === 6)) continue
    added++
  }
  return d
}

export function dedupeKey(owner, taskType, company, dueDateStr) {
  return [owner || '', taskType || '', (company || '').trim().toLowerCase(), dueDateStr || ''].join('|')
}

// Plan the full set of writes for a saved call, given the chosen outcome.
// `form`   = the conditional field values (strings).
// `lead`   = { agent, prospect_name, company, ...context }
// `todayStr` = YYYY-MM-DD in business tz (caller supplies, keeps this pure).
// Returns { callLogPatch, activity, task|null, cancelOutreach, suppress, stageTo }.
export function planCallSave({ outcomeId, form = {}, lead = {}, todayStr }) {
  const o = getOutcome(outcomeId)
  if (!o) throw new Error('Unknown outcome: ' + outcomeId)
  const agent = lead.agent
  const company = lead.company

  // 1) call_logs field mapping (keeps existing table shape)
  const callLogPatch = { ...o.callLog }
  if (outcomeId === 'interested' && form.bant_score != null && form.bant_score !== '') {
    callLogPatch.bant_score = parseInt(form.bant_score) || 0
  }
  if (form.next_action) callLogPatch.next_action = form.next_action

  // 2) stage effect
  let stageTo = o.stageTo
  if (outcomeId === 'interested' && (parseInt(form.bant_score) || 0) >= 3) stageTo = 'Qualified'
  if (outcomeId === 'not_interested' && WORKFLOW_CONFIG.notInterestedPolicy === 'closed_lost') stageTo = 'Closed - Lost'

  // 3) follow-up task
  let task = null
  if (o.task) {
    let dueAt
    if (o.task.when === 'field') {
      dueAt = form[o.task.field] ? new Date(form[o.task.field]) : null
    } else {
      dueAt = addDaysISO(todayStr, o.task.days || 1, !!o.task.businessDays)
    }
    const dueDateStr = dueAt ? dueAt.toLocaleDateString('en-CA') : ''
    task = {
      owner_agent: agent,
      task_type: o.task.type,
      status: 'open',
      priority: o.task.priority || 'medium',
      due_at: dueAt ? dueAt.toISOString() : null,
      title: o.task.title,
      description: form.need || form.next_action || form.meeting_purpose || null,
      source: 'automation',
      company_name: company,
      contact_name: lead.prospect_name || null,
      dedupe_key: dedupeKey(agent, o.task.type, company, dueDateStr),
    }
  }

  // 4) activity (audit/history)
  const activity = {
    actor_agent: agent,
    activity_type: 'call',
    company_name: company,
    contact_name: lead.prospect_name || null,
    outcome: o.label,
    notes: form.notes || form.reason || form.need || null,
  }

  return {
    callLogPatch,
    activity,
    task,
    stageTo,
    cancelOutreach: !!o.cancelOutreach,
    suppress: !!o.suppress,
    outreachTaskTypes: OUTREACH_TASK_TYPES,
  }
}
